import { uniq } from 'lodash';

import { webFontLoader } from 'hb-react/shared/utils/webFontLoader';

type GoogleFont = 'Manrope' | 'Puritan' | 'Sumana';

interface Option {
  label: string;
  value: number;
}

export const loadedFonts: Array<string> = [];

export const FONT_SIZES = [
  8, 9, 10, 11, 12, 14, 16, 20, 22, 24, 26, 28, 32, 36, 48, 72,
];

export const GOOGLE_FONTS_WHITE_LIST: Array<GoogleFont> = [
  'Manrope',
  'Puritan',
  'Sumana',
];

export const INITIAL_FONTS = [
  'Arial',
  'Helvetica Neue',
  'Verdana',
  'Georgia',
  'Tahoma',
  'Times New Roman',
  'Courier New',
];

export const EMAIL_INITIAL_FONTS = INITIAL_FONTS.filter(
  (font) => font !== 'Tahoma',
);

const isInitialFont = (font: string): boolean =>
  INITIAL_FONTS.some((initialFont) => initialFont === font);

export const loadFont = (font: string, availableFonts?: Array<string>) => {
  if (
    font &&
    !isInitialFont(font) &&
    !loadedFonts.includes(font) &&
    (!availableFonts || availableFonts.includes(font))
  ) {
    loadedFonts.push(font);
    webFontLoader({
      font,
    });
  }
};

export const mapFonts = (
  rawFonts: Array<string>,
): Array<{ label: string; value: string }> => {
  // uniq removes duplicates values
  const fonts = uniq(rawFonts);
  const fontSorted = fonts.sort();

  return fontSorted.map((font) => ({ label: font, value: font }));
};

export const valuesToOptions = (
  array: Array<number>,
  valueToLabel: (value: number) => string,
): Array<Option> =>
  array.map((value: number) => ({
    label: valueToLabel(value),
    value,
  }));

const traverseGetFontFamilies = (
  contentHtml,
  availableFonts,
  loaded = loadedFonts,
  customFontName = '',
) => {
  const extractedFontFamily = contentHtml.style?.fontFamily?.replace(
    /["']/g,
    '',
  );

  if (extractedFontFamily && extractedFontFamily !== customFontName) {
    loadFont(extractedFontFamily, availableFonts);
  }

  if (contentHtml.children?.length) {
    Array.from(contentHtml.children).forEach((child) =>
      traverseGetFontFamilies(child, undefined, loaded, customFontName),
    );
  }
};

const traverseDocumentForFonts = (
  item,
  availableFonts,
  customFontName = '',
) => {
  // Extract fonts from current item's attributes
  const rawFontAttributes =
    item?.attributes?.textFontFamily ||
    item?.attributes?.buttonFontFamily ||
    item?.attributes?.fontFamily;

  const fontAttributes = rawFontAttributes?.split(',') || [];

  fontAttributes.forEach((font) => {
    const finalFont = font.trim();

    if (finalFont !== customFontName) {
      loadFont(finalFont, availableFonts);
    }
  });

  // Extract fonts from content if it exists
  if (item.content) {
    const contentHtml = document.createElement('div');

    contentHtml.innerHTML = item.content;

    traverseGetFontFamilies(
      contentHtml,
      availableFonts,
      loadedFonts,
      customFontName,
    );
  }

  // Recursively process children
  if (item.children?.length) {
    item.children.forEach((child) =>
      traverseDocumentForFonts(child, availableFonts, customFontName),
    );
  }
};

export const extractAndLoadFonts = (
  document,
  availableFonts,
  customFontName = '',
) => {
  if (!document) {
    return;
  }

  traverseDocumentForFonts(document, availableFonts, customFontName);
};
